/**
 * ETERNITY INTELLIGENCE — ATMOSPHERIC BACKGROUND
 * Large blurred orange/amber/ember gradient fields with slow, calm motion
 * Based on reference: deep black with warm brown-black/red-black atmospheric zones
 */
import React from 'react';
import { motion } from 'framer-motion';

const AtmosphericBackground = ({ intensity = 'normal' }) => {
  // Intensity: subtle, normal, strong
  const intensityMap = {
    subtle: { opacity: 0.35, blur: 100 },
    normal: { opacity: 0.5, blur: 120 },
    strong: { opacity: 0.65, blur: 140 }
  };

  const config = intensityMap[intensity] || intensityMap.normal;

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
      {/* Base deep black */}
      <div className="absolute inset-0" style={{ background: '#000000' }} />

      {/* Large atmospheric gradient field - Top right (Orange/Ember) */}
      <motion.div
        className="absolute"
        style={{
          width: '1200px',
          height: '1200px',
          top: '-400px',
          right: '-300px',
          background: 'radial-gradient(circle at center, rgba(232, 80, 2, 0.5) 0%, rgba(193, 8, 1, 0.35) 30%, rgba(139, 6, 1, 0.2) 50%, transparent 70%)',
          filter: `blur(${config.blur}px)`,
          opacity: config.opacity
        }}
        animate={{
          x: [0, 80, 0],
          y: [0, -50, 0],
          scale: [1, 1.08, 1]
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Large atmospheric gradient field - Bottom left (Amber/Red) */}
      <motion.div
        className="absolute"
        style={{
          width: '1000px',
          height: '1000px',
          bottom: '-350px',
          left: '-250px',
          background: 'radial-gradient(circle at center, rgba(241, 96, 1, 0.4) 0%, rgba(217, 12, 58, 0.25) 35%, rgba(193, 8, 1, 0.15) 55%, transparent 75%)',
          filter: `blur(${config.blur}px)`,
          opacity: config.opacity
        }}
        animate={{
          x: [0, -60, 0],
          y: [0, 70, 0],
          scale: [1, 1.12, 1]
        }}
        transition={{
          duration: 28,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 3
        }}
      />

      {/* Center atmospheric field (Warm brown-black zone) */}
      <motion.div
        className="absolute"
        style={{
          width: '900px',
          height: '900px',
          top: '20%',
          left: '30%',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle at center, rgba(255, 97, 41, 0.3) 0%, rgba(241, 96, 1, 0.18) 40%, rgba(139, 6, 1, 0.1) 60%, transparent 75%)',
          filter: `blur(${config.blur}px)`,
          opacity: config.opacity * 0.85
        }}
        animate={{
          scale: [1, 1.15, 1],
          x: [0, 50, 0],
          y: [0, -40, 0]
        }}
        transition={{
          duration: 32,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 5
        }}
      />

      {/* Subtle ember glow - Center right */}
      <motion.div
        className="absolute"
        style={{
          width: '700px',
          height: '700px',
          top: '60%',
          right: '10%',
          background: 'radial-gradient(circle at center, rgba(255, 97, 41, 0.25) 0%, rgba(232, 80, 2, 0.15) 45%, transparent 70%)',
          filter: `blur(${config.blur * 0.9}px)`,
          opacity: config.opacity * 0.7
        }}
        animate={{
          scale: [1, 1.1, 1],
          x: [0, -40, 0]
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 8
        }}
      />

      {/* Subtle grain overlay for texture */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E")`,
          opacity: 0.4,
          mixBlendMode: 'overlay'
        }}
      />
    </div>
  );
};

export default AtmosphericBackground;
